/**
 * AGC-only mic graph.
 *
 * Publishes the raw mic through a `sendAgc`-only graph (no denoiser) so
 * send-side loudness leveling applies EVEN when the denoiser is off,
 * failed, or bypassed. Levels the OUTGOING mic toward a common target
 * just like the denoiser graphs do (deepfilternet.ts, rnnoise.ts) but
 * without the suppression stage.
 *
 * Why this exists (the bug it fixes):
 *   sendAgc is the only thing equalizing per-user loudness across
 *   participants. Until 1.1.168A it was built INSIDE each denoiser
 *   graph (deepfilternet.ts:481, rnnoise.ts:121), so a user whose
 *   denoiser was off / failed / bypassed (OS-denoiser detected, all
 *   cascade entries returned null, or `noiseSuppression=false` pref)
 *   published the RAW mic without any leveling — they could arrive
 *   3-5 dB hotter or quieter than the rest of the call. Diagnosed
 *   from prod voice_diag on the 2026-05-26 team call: Aleksei I,
 *   default user (no localStorage levers set), denoiser='idle' in
 *   ClickHouse → arrived avg -21.86 dBFS vs the call's median -25.
 *   This graph fills the gap so sendAgc applies independently of
 *   denoiser status.
 *
 * Wiring:
 *   rawTrack → ctx.createMediaStreamSource → sendAgc.input(tee) →
 *     sendAgc.makeup → sendAgc.fade → MediaStreamDestination → publish
 *
 * Mirrors `rnnoise.ts applyRNNoise` for context/lifecycle so the
 * existing voice invariants apply unchanged:
 *   - dedicated 48 kHz AudioContext (#9 — sample-rate hard requirement)
 *   - `ctx.state === 'running'` hard check (#10/#16)
 *   - registerAuxAudioContext so the gesture unlock (#18) and the
 *     health-check resume tick (#53) revive it if it suspends
 *   - destroy() stops the raw mic (#17 — Windows comms-ducking)
 */

import { registerAuxAudioContext, unregisterAuxAudioContext } from '../components/voice/audioContext'
import type { DenoiserHandle } from './denoiserBackend'
import { createSendAgc } from './sendAgc'

// Lazy 48 kHz context, mirroring rnnoise.ts. We do NOT reuse the
// shared playback context because (a) its sample rate is the system
// default (often 44.1 on Safari) and (b) reusing it couples send and
// receive lifecycles. A dedicated context is cheap (no worklet to
// register, just nodes) and isolates the AGC graph.
let initPromise: Promise<AudioContext> | null = null

async function ensureAgcOnlyCtx(): Promise<AudioContext> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    const ctx = new AudioCtx({ sampleRate: 48000 })
    // Hand to the gesture-unlock + visibilitychange + health-tick
    // resume registry. Without this a suspended ctx would silently
    // emit no samples and we'd publish encoded silence (#10).
    registerAuxAudioContext(ctx)
    return ctx
  })()
  initPromise.catch(() => { initPromise = null })
  return initPromise
}

/**
 * Mic-free prewarm. Builds the dedicated AGC-only AudioContext so the
 * first capture pays only the cheap per-graph node wiring. Idempotent.
 * Safe to fire from a login-success effect (NO getUserMedia here).
 */
export async function prewarmAgcOnly(): Promise<void> {
  if (typeof window === 'undefined') return
  try {
    await ensureAgcOnlyCtx()
  } catch (err) {
    console.warn('[agcOnly] prewarm failed (non-fatal):', err)
  }
}

/**
 * Wrap a raw mic track in a sendAgc-only graph. Returns a DenoiserHandle
 * compatible with the cascade so callers can treat it as a degenerate
 * "denoiser=none" backend. Throws if the AudioContext can't reach
 * 'running' (caller's cascade falls back to raw publish + browser NS).
 */
export async function applyAgcOnly(rawTrack: MediaStreamTrack): Promise<DenoiserHandle> {
  const ctx = await ensureAgcOnlyCtx()
  if (ctx.state === 'suspended') {
    try { await ctx.resume() } catch { /* noop */ }
  }
  // Same hard guard as rnnoise.ts:106 / deepfilternet.ts (#10/#16): a
  // suspended ctx publishes encoded silence at full Opus bitrate, peers
  // see bytes climbing with audioLevel=0. Refuse rather than ship that.
  // Drop the cached promise so the next call gets a fresh ctx that
  // might catch a later gesture window.
  if (ctx.state !== 'running') {
    initPromise = null
    // Close + unregister the abandoned non-running context (mirrors rnnoise.ts).
    // Without this, each suspend→replace cycle leaked a dead 48 kHz AudioContext
    // (native audio thread) that nothing closed — `unregisterAuxAudioContext` was
    // never called anywhere → the 2026-06-19 native RAM leak (~37 live contexts).
    try { unregisterAuxAudioContext(ctx) } catch { /* noop */ }
    try { void ctx.close() } catch { /* noop */ }
    throw new Error(`AGC-only AudioContext is ${ctx.state}; refusing to publish silent track`)
  }

  const inputStream = new MediaStream([rawTrack])
  const source = ctx.createMediaStreamSource(inputStream)
  const sendAgc = createSendAgc(ctx)
  const destination = ctx.createMediaStreamDestination()

  source.connect(sendAgc.input)
  sendAgc.output.connect(destination)

  const processedTrack = destination.stream.getAudioTracks()[0]
  if (!processedTrack) {
    try { sendAgc.destroy() } catch { /* noop */ }
    try { source.disconnect() } catch { /* noop */ }
    try { destination.disconnect() } catch { /* noop */ }
    throw new Error('[agcOnly] destination produced no output track')
  }

  return {
    track: processedTrack,
    destroy: () => {
      try { sendAgc.destroy() } catch { /* noop */ }
      try { source.disconnect() } catch { /* noop */ }
      try { destination.disconnect() } catch { /* noop */ }
      // Stop the raw mic that fed this graph. Mirrors rnnoise.ts:145 —
      // without this the OS capture stays active after teardown and
      // Windows triggers the "communications" ducking policy on other
      // audio sources (#17). The raw track is internal to this handle —
      // the caller never sees it directly.
      try { rawTrack.stop() } catch { /* noop */ }
      try { processedTrack.stop() } catch { /* noop */ }
    },
  }
}
